/**
 * Resolution adaptative: decider, seulement.
 *
 * Module PUR — ni DOM, ni three.js, ni etat cache. Il ne fait que repondre a la
 * question « faut-il changer de resolution, et pour laquelle ? ». C'est ce qui
 * le rend testable: on lui rejoue une centaine de secondes d'images par seconde
 * et on regarde ce qu'il decide, sans navigateur.
 *
 * POURQUOI IL EXISTE. La regle precedente montait des que les images par seconde
 * depassaient le seuil haut, sans se souvenir qu'elle venait d'echouer a ce
 * niveau-la. Sur une machine qui tient tout juste, ca produit un cycle sans fin:
 * on monte, on etouffe, on redescend, on respire, on remonte. Le journal d'une
 * seule partie du proprietaire compte 167 changements de resolution, dont 42
 * montees et 43 descentes entre exactement les deux memes valeurs.
 *
 * Et le cycle se paie deux fois: chaque changement reconstruit les cibles de
 * rendu, donc la correction elle-meme coute des images. Une resolution un cran
 * trop basse mais STABLE vaut mieux qu'une resolution juste qui clignote.
 */

/** Resolution minimale: en dessous, l'image devient trop molle. */
export const RATIO_MIN = 0.75
export const RATIO_PAS = 0.25
/** Sous ce nombre d'images par seconde, on baisse la resolution. */
export const FPS_BAISSE = 48
/** Au-dessus, on la remonte si on l'avait baissee. */
export const FPS_MONTE = 58
/** Secondes calmes exigees avant une premiere montee. */
export const PATIENCE_BASE = 4
/** Plancher de patience des qu'un niveau a echoue une fois. */
export const PATIENCE_APRES_ECHEC = 30
/** Plafond de patience: au-dela, on a compris, on ne retente plus si souvent. */
export const PATIENCE_MAX = 240

export interface EtatResolution {
  ratio: number
  secondesLentes: number
  secondesRapides: number
  /** Premier niveau qui a etouffe la machine. `Infinity` si aucun. */
  ratioEtouffe: number
  /** Secondes calmes exigees avant de retenter ce niveau; double a chaque echec. */
  patience: number
}

export function etatResolutionNeuf(ratio = 1): EtatResolution {
  return { ratio, secondesLentes: 0, secondesRapides: 0, ratioEtouffe: Infinity, patience: PATIENCE_BASE }
}

/**
 * Un pas de decision, appele une fois par seconde.
 *
 * Modifie `e` sur place et renvoie la nouvelle resolution si elle a change,
 * sinon null. Le plafond materiel est celui de l'ecran.
 */
export function deciderResolution(e: EtatResolution, fps: number, plafondMateriel: number): number | null {
  if (fps < FPS_BAISSE && e.ratio > RATIO_MIN) {
    e.secondesRapides = 0
    if (++e.secondesLentes < 2) return null
    e.secondesLentes = 0
    // Ce niveau ne passe pas: on s'en souvient, et on redoublera de patience
    // avant de le retenter.
    e.ratioEtouffe = Math.min(e.ratioEtouffe, e.ratio)
    /*
     * Reculer VITE apres un echec. Doubler paraissait prudent, mais les
     * premieres tentatives restaient rapprochees (4 s, 8 s, 16 s) et c'est
     * precisement la que le clignotement se voit. On quadruple, avec un plancher
     * d'une demi-minute: la premiere retentative arrive assez tard pour que le
     * joueur ne remarque rien, et la suite s'espace encore.
     */
    e.patience = Math.min(Math.max(e.patience * 4, PATIENCE_APRES_ECHEC), PATIENCE_MAX)
    e.ratio = Math.max(RATIO_MIN, e.ratio - RATIO_PAS)
    return e.ratio
  }

  if (fps > FPS_MONTE && e.ratio < plafondMateriel) {
    e.secondesLentes = 0
    e.secondesRapides++
    // Tant qu'on n'a jamais echoue au-dessus, la patience de base suffit. Sinon
    // il faut avoir tenu la duree apprise pour avoir le droit de retenter.
    const viseNiveauRate = e.ratio + RATIO_PAS >= e.ratioEtouffe
    const requis = viseNiveauRate ? e.patience : PATIENCE_BASE
    if (e.secondesRapides < requis) return null
    e.secondesRapides = 0
    /*
     * On oublie l'echec en remontant: si la machine tient de nouveau ce niveau,
     * c'est que les conditions ont change (on a quitte la foret pour la plage) et
     * rien ne doit plus empecher de monter encore. La patience apprise, elle,
     * reste doublee: on retentera moins vite si ca rate a nouveau.
     */
    if (viseNiveauRate) e.ratioEtouffe = Infinity
    e.ratio = Math.min(plafondMateriel, e.ratio + RATIO_PAS)
    return e.ratio
  }

  e.secondesLentes = 0
  e.secondesRapides = 0
  return null
}
